import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import { useKeyboardShortcuts, useToast } from '@/hooks/useKeyboardShortcuts.js';
import {
  ASIGNACION_TURNO_BY_ID,
  ASIGNACION_TURNO_CREATE,
  ASIGNACION_TURNO_UPDATE,
} from '@/graphql/rrhh/turnos.gql.js';

const ESTADOS = [
  { label: 'Activa', value: 'Activa' },
  { label: 'Pausada', value: 'Pausada' },
  { label: 'Finalizada', value: 'Finalizada' },
];

const INITIAL_STATE = {
  empleadoId: '',
  turnoId: '',
  desde: '',
  hasta: '',
  estado: 'Activa',
  sucursalId: '',
};

function validate(form) {
  const errors = {};
  if (!form.empleadoId) {
    errors.empleadoId = 'Indicá el colaborador asignado.';
  }
  if (!form.turnoId) {
    errors.turnoId = 'Seleccioná un turno.';
  }
  if (!form.desde) {
    errors.desde = 'Definí la fecha de inicio.';
  }
  if (form.desde && form.hasta && form.hasta < form.desde) {
    errors.hasta = 'La fecha de fin debe ser posterior a la de inicio.';
  }
  return errors;
}

export default function FormAsignacionTurno() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const toast = useToast();
  const formRef = useRef(null);

  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [{ data, fetching }] = useQuery({
    query: ASIGNACION_TURNO_BY_ID,
    variables: { id },
    pause: !isEdit,
  });

  const [, createAsignacion] = useMutation(ASIGNACION_TURNO_CREATE);
  const [, updateAsignacion] = useMutation(ASIGNACION_TURNO_UPDATE);

  useEffect(() => {
    if (data?.asignacionTurno) {
      setForm({
        empleadoId: data.asignacionTurno.empleadoId?.toString() || '',
        turnoId: data.asignacionTurno.turnoId?.toString() || '',
        desde: data.asignacionTurno.desde || '',
        hasta: data.asignacionTurno.hasta || '',
        estado: data.asignacionTurno.estado || 'Activa',
        sucursalId: data.asignacionTurno.sucursalId?.toString() || '',
      });
    }
  }, [data]);

  useKeyboardShortcuts(() => {
    formRef.current?.requestSubmit();
  });

  const pageTitle = useMemo(() => (isEdit ? 'Editar asignación' : 'Nueva asignación'), [isEdit]);

  const breadcrumbs = useMemo(() => [
    { label: 'RRHH', href: '/rrhh' },
    { label: 'Turnos', href: '/rrhh/turnos' },
    { label: isEdit ? 'Editar asignación' : 'Nueva asignación' },
  ], [isEdit]);

  const handleChange = (field) => (event) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const validation = validate(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      toast.warning('Revisá los campos marcados antes de continuar.');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      empleadoId: Number(form.empleadoId),
      turnoId: Number(form.turnoId),
      desde: form.desde,
      hasta: form.hasta || null,
      estado: form.estado,
      sucursalId: form.sucursalId ? Number(form.sucursalId) : null,
    };

    const result = isEdit
      ? await updateAsignacion({ id, input: payload })
      : await createAsignacion({ input: payload });

    setIsSubmitting(false);

    if (result.error) {
      toast.error(result.error?.message || 'No pudimos guardar la asignación.');
      return;
    }

    toast.success(isEdit ? 'Actualizamos la asignación.' : 'Creamos la asignación.');
    navigate('/rrhh/turnos');
  };

  return (
    <div className="w-full">
      <PageHeader
        title={pageTitle}
        description="Relacioná personas con turnos definidos y controlá su vigencia."
        breadcrumbs={breadcrumbs}
      />

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="mx-auto grid w-full max-w-[1400px] grid-cols-12 gap-6"
      >
        <section className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.95)] p-6 shadow-[0_8px_30px_rgba(var(--shadow),0.15)]">
          <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Datos de la asignación</h2>
          <div className="mt-4 grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <Field label="Empleado" htmlFor="empleadoId" required error={errors.empleadoId}>
                <input
                  id="empleadoId"
                  value={form.empleadoId}
                  onChange={handleChange('empleadoId')}
                  placeholder="ID colaborador"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field label="Turno" htmlFor="turnoId" required error={errors.turnoId}>
                <input
                  id="turnoId"
                  value={form.turnoId}
                  onChange={handleChange('turnoId')}
                  placeholder="ID turno"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field label="Fecha desde" htmlFor="desde" required error={errors.desde}>
                <input
                  id="desde"
                  type="date"
                  value={form.desde}
                  onChange={handleChange('desde')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field label="Fecha hasta" htmlFor="hasta" error={errors.hasta} hint="Dejalo vacío si la asignación sigue vigente">
                <input
                  id="hasta"
                  type="date"
                  value={form.hasta}
                  onChange={handleChange('hasta')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field label="Estado" htmlFor="estado">
                <select
                  id="estado"
                  value={form.estado}
                  onChange={handleChange('estado')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {ESTADOS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field label="Sucursal" htmlFor="sucursalId" hint="Opcional">
                <input
                  id="sucursalId"
                  value={form.sucursalId}
                  onChange={handleChange('sucursalId')}
                  placeholder="ID sucursal"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
          </div>
          {fetching ? (
            <p className="mt-4 text-sm text-[rgba(var(--muted),0.9)]">Cargando datos de la asignación...</p>
          ) : null}
        </section>
      </form>

      <StickyActions
        onCancel={() => navigate('/rrhh/turnos')}
        onSubmit={() => formRef.current?.requestSubmit()}
        isSubmitting={isSubmitting}
        primaryLabel={isEdit ? 'Guardar cambios' : 'Crear asignación'}
      />
    </div>
  );
}
